import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { CometChatUIKit } from '@cometchat/chat-uikit-react';
import { isValidUUID } from 'views/Dashboard/common/utils';
import { AuthContext } from 'context/AuthProvider';

export const AppContext = createContext();

export const useAppContext = () => useContext(AppContext);

const AppContextProvider = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [scrollPositions, setScrollPositions] = useState({});
  const [isChatInitializing, setChatInitializing] = useState(true);
  const [isChatAuthenticated, setChatAuthenticated] = useState(false);
  const isChatInitiated = useRef(false);
  
  // Use AuthContext directly instead of useAuth hook to avoid circular dependency
  // This works because AppContextProvider is used inside AuthProvider in AppLayout
  const authContext = useContext(AuthContext);
  const auth = authContext?.auth || null;

  const initializeChat = useCallback(
    async (userId) => {
      console.log('initializing chat...');

      // Prevent multiple simultaneous initialization attempts
      if (isChatInitiated.current || isChatAuthenticated) {
        console.warn('Chat initialization already in progress or logged in');
        return;
      }

      // Set initialization flags
      isChatInitiated.current = true;

      try {
        if (!userId) {
          console.warn('User ID required for Chat login');
          return;
        }

        if (!isValidUUID(userId)) {
          console.error('Invalid UUID provided for chat initialization');
          return;
        }

        // Check if already logged in
        const existingUser = await CometChat.getLoggedinUser();

        if (existingUser) {
          if (existingUser?.getUid() === userId) {
            console.log(
              'User already logged in with correct ID:',
              existingUser
            );
            setChatAuthenticated(true);
            return;
          } else {
            console.warn('Different uid. Logging out...');
            await CometChat.logout();
          }
        }

        await CometChatUIKit.login(userId);
        console.log('Chat Login successful');
        // const loggedInUser = await CometChatUIKit.login(userId);
        // console.log('Chat Login successful:', loggedInUser);
        setChatAuthenticated(true);
      } catch (error) {
        console.error('Chat Login Error:', error);
        setChatAuthenticated(false);
      } finally {
        // Reset initialization flag
        setChatInitializing(false);
        isChatInitiated.current = false;
      }
    },
    [isChatAuthenticated]
  );

  useEffect(() => {
    initializeChat(auth?.user_id);
  }, [auth?.user_id, initializeChat]);

  const logoutChat = async () => {
    try {
      await Promise.all([CometChatUIKit.logout(), CometChat.logout()]);
      setChatAuthenticated(false);
      console.log('Logout completed successfully from both UI Kit and JS SDK.');
    } catch (error) {
      console.error('Logout failed with exception:', error);
    }
  };

  return (
    <AppContext.Provider
      value={{
        isSidebarOpen,
        setSidebarOpen,
        isMobileSidebarOpen,
        setMobileSidebarOpen,
        scrollPositions,
        setScrollPositions,
        isChatInitializing,
        isChatAuthenticated,
        initializeChat,
        logoutChat,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
